import React, { useState } from 'react';
import MessageModal from 'components/features/modal/MessageModal';
import Icon from 'components/commons/icon/Icon';
import NumberLabel from 'components/features/utils/NumberLabel';
import { formatNumber, formatDate } from 'utils/formatter';
import { getDate } from 'components/features/utils/converter';

import * as api from 'config/api';
import * as consts from 'config/consts';

import { SlideDayType } from 'pages/AssetTransition';
import { useModal } from 'hooks/useModal';
import { AggregateDataErrorModal } from '../modal/AggregateDataErrorModal';

const C_Message = {
  title: '',
  message: [
    '「みんかぶ口座連携」で連携した口座は土日祝日はデータ更新を行いません。',
    '前回取得成功したデータを暫定値として表示しています。',
  ],
};

type AssetAllocationInfoProps = {
  assetList: Array<api.GetAssetResultItem> | undefined;
  prevAsset: api.GetAssetResultItem | undefined;
  dateSelectIndex: number;
  today: number;
  slideList: Array<SlideDayType> | undefined;
};

type Asset = {
  day: number;
  totalAssetAmount: number | null;
  changed: number | null;
  slideData: SlideDayType | undefined;
  incorrect: boolean;
};

const AssetItem = ({
  asset,
  dateSelectIndex,
  onInfoClick,
  myKey,
}: {
  asset: Asset;
  dateSelectIndex: number;
  onInfoClick: () => void;
  myKey: number;
}) => {
  const isYear = dateSelectIndex === consts.ChartPeriodTypes.年;
  const fmt = isYear ? 'M月' : 'M/d(ddd)';
  const date = formatDate(getDate(asset.day), fmt);
  const [
    isShowTestModal,
    { hideModal: hideTestModal, showModal: showTestModal },
  ] = useModal();

  return (
    <>
      <tr className="border-b border-gray-300" key={myKey}>
        <td className="flex flex-row items-center text-left py-2 pl-3 text-sm">
          <div>{date}</div>
          {asset.incorrect === true && (
            <div
              className="icon-info text-sm ml-1 text-bluegray-400 cursor-pointer"
              onClick={showTestModal}></div>
          )}
        </td>
        <td className="text-right py-2 pr-3">
          {formatNumber(asset.totalAssetAmount, {})}
        </td>
        <td
          className="flex justify-end py-2 pr-3 text-sm cursor-pointer"
          onClick={asset.slideData ? onInfoClick : undefined}>
          {asset.slideData && (
            <span className="text-gray-500 pr-1">
              <Icon name="info" />
            </span>
          )}
          <NumberLabel value={asset.changed} />
        </td>
      </tr>
      {asset.incorrect === true && (
        <AggregateDataErrorModal
          hide={hideTestModal}
          isShow={isShowTestModal}
          day={asset.day}
          isYear={isYear}
        />
      )}
    </>
  );
};

const AssetAllocationInfo: React.FC<AssetAllocationInfoProps> = ({
  assetList,
  prevAsset,
  dateSelectIndex,
  today,
  slideList,
}) => {
  const [showModal, setShowModal] = useState(false);
  let preTotalAssetAmount: number | null = prevAsset?.TotalAssetAmount ?? null;

  let slides: Array<SlideDayType> = [];
  if (assetList && assetList?.length > 0 && slideList) {
    const st = assetList.length - 1;
    slides = slideList?.filter(
      s => s.Day >= assetList[st].Day && s.Day <= assetList[0].Day
    );
  }

  // assetListは日付降順なので、日付昇順にした別配列を用意
  const _assetList = assetList
    ?.map(x => {
      return {
        Day: x.Day,
        TotalAssetAmount: x.TotalAssetAmount,
        Incorrect: x.Incorrect,
      };
    })
    .reverse();

  // 昇順で変動額を算出
  const assets = _assetList
    ?.map(x => {
      // 変動額
      const changed =
        x.TotalAssetAmount == null || preTotalAssetAmount == null
          ? null
          : x.TotalAssetAmount - preTotalAssetAmount;
      // 値がなければ、そのまま
      if (x.TotalAssetAmount !== null) {
        preTotalAssetAmount = x.TotalAssetAmount;
      }
      return {
        day: x.Day,
        totalAssetAmount: x.TotalAssetAmount,
        changed: changed,
        slideData: slides.find(s => s.Day === x.Day),
        incorrect: x.Incorrect,
      };
    })
    .reverse();

  const onInfoClick = () => setShowModal(true);
  const onClose = () => setShowModal(false);

  const isAssetData = assetList && assetList?.length > 0;

  return (
    <div className={`mx-4 mt-2 mb-4 ${isAssetData ? '' : 'hidden'}`}>
      <div className="text-right text-gray-500 text-xs pb-2">単位: 円</div>
      <table className="w-full">
        <thead>
          <tr className="sticky border-b border-gray-300 bg-gray-300">
            <th className="text-left text-gray-700 text-sm pt-1 pl-6">日付</th>
            <th className="text-right text-gray-700 text-sm pt-1 pr-3">
              資産金額
            </th>
            <th className="text-right text-gray-700 text-sm pt-1 pr-6">
              変動額
            </th>
          </tr>
        </thead>
        <tbody>
          {assets?.map((x, i) => {
            if (today < x.day) return false;
            return (
              <>
                <AssetItem
                  asset={x}
                  dateSelectIndex={dateSelectIndex}
                  onInfoClick={onInfoClick}
                  myKey={i}
                />
              </>
            );
          })}
        </tbody>
      </table>
      <div className="my-3 text-sm text-gray-500">
        {dateSelectIndex === consts.ChartPeriodTypes.年 ? (
          <div>
            資産金額・変動額は月末終値より算出。
            <br />
            但し当月は月内最新データから算出。
          </div>
        ) : (
          <div>資産金額・変動額は終値より算出。</div>
        )}
      </div>
      {showModal && (
        <div onClick={() => showModal && onClose()}>
          <MessageModal
            title={C_Message.title}
            message={C_Message.message}
            onClose={onClose}
          />
        </div>
      )}
    </div>
  );
};

export default AssetAllocationInfo;
